/**
 * Protected Route Component
 * 
 * Wraps routes that require authentication or specific roles.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { persistLoginFrom } from '../utils/auth';
import { canUseDesigner } from '../utils/tierEntitlements';

/**
 * Protects a route, redirecting to login if not authenticated
 */
export function ProtectedRoute({ children, requireAdmin = false, requireSuperAdmin = false, requireDesigner = false }) {
    const { isAuthenticated, isLoading, isAdmin, isSuperAdmin, cloudAvailable, user } = useAuth();
    const location = useLocation();

    // Show loading state
    if (isLoading) {
        return (
            <div className="auth-loading">
                <div className="spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    // If cloud is not available, show message
    if (!cloudAvailable) {
        return (
            <div className="cloud-unavailable">
                <div className="icon">☁️</div>
                <h2>Cloud Backend Unavailable</h2>
                <p>Cannot connect to the cloud backend at this time.</p>
                <p>Make sure the Docker containers are running.</p>
                <button onClick={() => window.location.reload()}>
                    Retry Connection
                </button>
            </div>
        );
    }

    // Check authentication
    if (!isAuthenticated) {
        persistLoginFrom(location);
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Check admin requirement
    if (requireSuperAdmin && !isSuperAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    if (requireAdmin && !isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    if (requireDesigner && !canUseDesigner(user)) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

/**
 * Public route that redirects to dashboard if already authenticated
 */
export function PublicRoute({ children }) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="auth-loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

export default ProtectedRoute;

